/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useField } from 'formik';
import dayjs from 'dayjs';
import Error from './Error';
import TooltipComponent from '../../TooltipComponent';
import { ISimpleSelectOption } from '../ArrayFields/interfaces';

interface ISimpleSelectProps {
  name: string;
  label: string;
  options?: ISimpleSelectOption[];
  width?: string;
  introDisabled?: boolean;
  hidden?: boolean;
  id: string;
  disabled?: boolean;
  className?: string;
  queueId?: string;
  introOption?: string;
  required?: boolean;
  onChangeCallback?: (value: any) => void;
  onFocusCallback?: () => void;
  newStyle?: boolean;
  tooltip?: string;
  labelClassName?: string;
  rowClassName?: string;
  columnClassName?: string;
  note?: string;
  isChangeDetail?: string;
}

const convertValue = (item: string) => {
  let newItem = '';
  const regexFindDate = new RegExp(/[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}/gi);
  if (regexFindDate.test(item)) {
    const [date, ...info] = item.split(' - ');
    if (date) {
      const dateAfterSplit = date.split(' ');
      if (dateAfterSplit && dateAfterSplit.length === 2) {
        const newDateString = `${dateAfterSplit[0]}T${dateAfterSplit[1]}Z`;
        const regexFindDateString = new RegExp(
          /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z/gi
        );
        if (regexFindDateString.test(newDateString)) {
          const formattedDate = dayjs(newDateString).format('DD.MM.YYYY HH:mm');
          newItem = info ? `${formattedDate} - ${info.join(' - ')}` : formattedDate;
        }
      }
    }
  }
  return newItem || item;
};

export const getOptionByValue = (
  options: ISimpleSelectOption[],
  optionValue: string
): string | null => {
  const option = options.find((item: ISimpleSelectOption) => String(item.value) === optionValue);
  if (option) {
    return convertValue(option.item);
  }
  return null;
};

function SimpleSelect(props: ISimpleSelectProps): JSX.Element {
  const [field, meta, handleChange] = useField(props);
  const [options, setOptions] = React.useState<ISimpleSelectOption[]>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const selectWidth = props.width ? props.width : 'col-sm-9';

  React.useEffect(() => {
    if (props.options) {
      let newOptions: ISimpleSelectOption[] | null = null;
      if (props.introOption && props.newStyle) {
        const introOptionItem = [{ item: props.introOption, value: '' }];
        newOptions = introOptionItem.concat(props.options);
      }
      setOptions(newOptions || props.options);
    }
  }, [props.options]);

  const handleClickOutside = React.useCallback(
    (event: any) => {
      const { id } = event.target;
      if (id === `simpleSelect-${props.name}` || id === `span-${props.name}`) {
        // setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    },
    [isOpen]
  );

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        if (isOpen) {
          setIsOpen(false);
        }
      }
    },
    [isOpen]
  );

  React.useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClickOutside]);

  const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.currentTarget;
    handleChange.setValue(value);
    if (props.onChangeCallback) {
      props.onChangeCallback(value);
    }
  };

  const handleOnFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    if (props.onFocusCallback) {
      props.onFocusCallback();
    }
  };

  const renderIntroOption = (introOption: string | undefined) => {
    if (props.introOption) {
      return (
        <option value="" disabled={props.introDisabled}>
          {props.introOption}
        </option>
      );
    }
    return <></>;
  };

  let errBorder = '';
  if (meta.touched && meta.error) {
    errBorder = 'err-border';
  }

  const handleOpen = (
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
  ): boolean => {
    event.preventDefault();
    if (props.disabled) {
      return false;
    }
    setIsOpen(!isOpen);
    if (isOpen) {
      handleChange.setTouched(true);
    }
    return true;
  };

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    const value = event.currentTarget.getAttribute('data-value');
    // if (value) {
    handleChange.setValue(value);
    if (props.onChangeCallback) {
      props.onChangeCallback(value);
    }
    setIsOpen(false);
    // }
  };

  const oldStyleContent: JSX.Element = (
    <div className="simple-select-component">
      <div className="form-group" style={props.hidden ? { display: 'none' } : { display: '' }}>
        <div className="text-left font-weight-bold col-sm-3 control-label">
          <label htmlFor={props.id} className="font-weight-bold">
            {props.required && (
              <span className="text-danger text-danger required-field-info">* </span>
            )}
            {props.label}
          </label>
        </div>

        <div className={selectWidth}>
          <div className="e-select">
            <select
              {...field}
              id={props.id}
              hidden={props.hidden}
              className={`${props.className} ${errBorder}`}
              disabled={props.disabled}
              name={props.name}
              queue-id={props.queueId}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              data-cy={`simple-select-${props.name}`}
              title={props.tooltip || ''}
            >
              {renderIntroOption(props.introOption)}
              {options &&
                options.length > 0 &&
                options.map((option: ISimpleSelectOption) => (
                  <option key={option.value} value={option.value}>
                    {option.item}
                  </option>
                ))}
            </select>
          </div>
          {meta.touched && meta.error && <Error message={meta.error} />}
        </div>
      </div>
    </div>
  );

  const newStyleContent: JSX.Element = (
    <div className={`form-group ${meta.touched && meta.error && 'form-group-error'}`}>
      <div className={props.rowClassName || ''}>
        <label className={`${props.labelClassName || 'd-block form-label'}`} onClick={handleOpen}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
          {props.note && (
            <>
              <br />
              <small dangerouslySetInnerHTML={{ __html: props.note }} />
            </>
          )}
        </label>
        <div className={props.columnClassName || 'd-block'}>
          <div className={`b-dropdown ${isOpen ? 'open' : ''}`}>
            <div
              id={`simpleSelect-${props.name}`}
              data-cy={`simpleSelect-${props.name}`}
              className="form-control-labels"
              onClick={handleOpen}
              title={props.tooltip || ''}
              style={props.disabled ? { color: '#bbbbbb' } : {}}
            >
              {props.isChangeDetail ? (
                <TooltipComponent
                  id={props.id}
                  firstItems={true}
                  width="180px"
                  positionRight={true}
                  overlay={<div style={{ color: '#fff' }}>{props.isChangeDetail}</div>}
                >
                  <span
                    style={{
                      background: '#3D72C8',
                      padding: '2px 10px',
                      borderRadius: '11px',
                      opacity: 0.3,
                      color: '#000',
                    }}
                  >
                    {field.value && props.options
                      ? getOptionByValue(props.options, field.value)
                        ? getOptionByValue(props.options, field.value)
                        : props.introOption
                      : props.introOption}
                  </span>
                </TooltipComponent>
              ) : (
                <span id={`span-${props.name}`}>
                  {field.value && props.options
                    ? getOptionByValue(props.options, field.value)
                      ? getOptionByValue(props.options, field.value)
                      : props.introOption
                    : props.introOption}
                </span>
              )}
            </div>
            {isOpen && !props.disabled && (
              <div className="b-dropdown__content" style={{ maxHeight: '300px', overflow: 'auto' }}>
                <ul className="b-dropdown__list simple-list">
                  {options &&
                    options.map((option: ISimpleSelectOption) => (
                      <li key={`key-${props.name}-${option.value}`}>
                        <a
                          href="#"
                          title={option.tooltip || option.item}
                          className="item"
                          data-cy={`simpleSelect-${props.name}-${option.item}`}
                          data-value={option.value}
                          onClick={handleOnClick}
                          style={
                            String(option.value) === String(field.value)
                              ? {
                                  backgroundColor: '#D1E9FF',
                                }
                              : {}
                          }
                        >
                          {convertValue(option.item)} {option.label ? `(${option.label})` : ''}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {meta.touched && meta.error && <Error message={meta.error} newStyle={true} />}
          </div>
        </div>
      </div>
    </div>
  );

  return props.newStyle ? newStyleContent : oldStyleContent;
}

export default SimpleSelect;
